/**
 * F&O Trade Analysis — the 5 breakdown accordions below the hero card:
 *   1. Trend & Momentum Diagnostics
 *   2. Futures & Basis Analytics
 *   3. Options Chain & OI Concentration
 *   4. Score Component Weight Breakdown (chart)
 *   5. Data Staleness & Risk Warnings
 *
 * Reads directly off `FnoTradeAnalysisResponse.components`/`data_quality` —
 * see backend `app.services.fno_analysis.scoring` module docstring for the
 * 7-component weighting this mirrors (`trend`/`momentum`/
 * `volume_volatility`/`price_action`/`futures_oi`/`options_oi`/
 * `market_context`, weights 20/15/10/15/15/15/10 summing to 100).
 *
 * Per-component `detail` is an untyped `Dict[str, Any]` on the backend (see
 * `detailFormatting.ts`'s own docstring) — sections 1-3 below extract the
 * handful of fields this codebase's `scoring.py` is documented to always
 * populate (regime, basis_pct, pcr, max_pain, OI-wall distances) for
 * plain-language display, then ALSO render the full flattened detail below
 * that for full transparency, so nothing the backend computed is silently
 * dropped even if a field this component doesn't specifically know about
 * gets added later.
 *
 * Chart choice (design decision, not fully specified in the brief): a
 * horizontal Chart.js bar chart (points scored vs. max points) for the
 * Score Component Weight Breakdown — simpler and safer to wire correctly
 * than a radar chart under time constraints, and reuses this codebase's
 * only installed charting library/pattern (see `PayoffChart.tsx`/
 * `AlgoPerformanceTab.tsx`).
 */
import { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { FnoComponentScore, FnoTradeAnalysisResponse } from "@/types";
import { regimeColor, regimeLabel } from "@/pages/options-dashboard/colors";
import { asNumber, asString, flattenDetail, getNested } from "./detailFormatting";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const COMPONENT_LABELS: Record<string, string> = {
  trend: "Trend",
  momentum: "Momentum",
  volume_volatility: "Volume & Volatility",
  price_action: "Price Action & Structure",
  futures_oi: "Futures & OI Dynamics",
  options_oi: "Options & OI Structure",
  market_context: "Market Context & Relative Strength",
};

function componentLabel(name: string): string {
  return COMPONENT_LABELS[name] ?? name;
}

function findComponent(components: FnoComponentScore[], name: string): FnoComponentScore | undefined {
  return components.find((c) => c.name === name);
}

const REGIME_EXPLANATIONS: Record<string, string> = {
  LONG_BUILDUP: "Long Buildup: price and open interest are both rising — new long positions are being added.",
  SHORT_BUILDUP: "Short Buildup: price is falling while open interest rises — new short positions are being added.",
  LONG_UNWINDING: "Long Unwinding: price is falling while open interest declines — existing longs are being closed out.",
  SHORT_COVERING: "Short Covering: price is rising while open interest declines — existing shorts are being bought back.",
  NEUTRAL: "Neutral: no clear directional buildup or unwinding pattern in futures open interest.",
};

function DetailRows({ detail }: { detail: Record<string, unknown> | undefined }) {
  const rows = flattenDetail(detail);
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No further detail available.
      </Typography>
    );
  }
  return (
    <Grid container spacing={1}>
      {rows.map((row) => (
        <Grid item xs={12} sm={6} md={4} key={row.label}>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.label}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {row.value}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

function TrendMomentumAccordion({ components }: { components: FnoComponentScore[] }) {
  const trend = findComponent(components, "trend");
  const momentum = findComponent(components, "momentum");
  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>
          Trend & Momentum Diagnostics
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Trend {trend ? `— ${trend.points.toFixed(1)} / ${trend.max_points.toFixed(0)} pts` : ""}
            </Typography>
            <DetailRows detail={trend?.detail} />
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Momentum {momentum ? `— ${momentum.points.toFixed(1)} / ${momentum.max_points.toFixed(0)} pts` : ""}
            </Typography>
            <DetailRows detail={momentum?.detail} />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function FuturesBasisAccordion({ components }: { components: FnoComponentScore[] }) {
  const futuresOi = findComponent(components, "futures_oi");
  const detail = futuresOi?.detail;
  const basisPct = asNumber(getNested(detail, "basis_pct"));
  const regime = asString(getNested(detail, "regime"));

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>
          Futures & Basis Analytics
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!detail || Object.keys(detail).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Futures & OI data unavailable for this instrument — see the Data Staleness & Risk Warnings section below.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Futures Basis
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {basisPct !== null ? `${basisPct >= 0 ? "+" : ""}${basisPct.toFixed(2)}%` : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Buildup / Unwinding Regime
                </Typography>
                <Chip
                  size="small"
                  label={regimeLabel(regime)}
                  sx={{
                    bgcolor: regimeColor(regime),
                    color: "#fff",
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                />
              </Grid>
            </Grid>
            {regime && (
              <Alert severity="info" variant="outlined">
                {REGIME_EXPLANATIONS[regime] ?? `${regimeLabel(regime)}: regime classification from bar-over-bar futures price/OI change.`}
              </Alert>
            )}
            <Divider />
            <DetailRows detail={detail} />
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function OptionsOiAccordion({ components }: { components: FnoComponentScore[] }) {
  const optionsOi = findComponent(components, "options_oi");
  const detail = optionsOi?.detail;
  const pcr = asNumber(getNested(detail, "pcr", "fresh_pcr_local"));
  const maxPainStrike = asNumber(getNested(detail, "max_pain", "max_pain_strike"));
  const supportDistance = asNumber(getNested(detail, "oi_walls", "support_distance_pct"));
  const resistanceDistance = asNumber(getNested(detail, "oi_walls", "resistance_distance_pct"));

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>
          Options Chain & OI Concentration
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!detail || Object.keys(detail).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Options OI data unavailable for this instrument — see the Data Staleness & Risk Warnings section below.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Put-Call Ratio (PCR)
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {pcr !== null ? pcr.toFixed(2) : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Max Pain Strike
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {maxPainStrike !== null ? maxPainStrike.toLocaleString("en-IN") : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Nearest Support Wall
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {supportDistance !== null ? `${supportDistance.toFixed(2)}% away` : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Nearest Resistance Wall
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {resistanceDistance !== null ? `${resistanceDistance.toFixed(2)}% away` : "—"}
                </Typography>
              </Grid>
            </Grid>
            <Divider />
            <DetailRows detail={detail} />
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function ScoreWeightAccordion({ components }: { components: FnoComponentScore[] }) {
  const chartData = useMemo(
    () => ({
      labels: components.map((c) => componentLabel(c.name)),
      datasets: [
        {
          label: "Points scored",
          data: components.map((c) => c.points),
          backgroundColor: "#1565C0",
        },
        {
          label: "Max points (weight)",
          data: components.map((c) => c.max_points),
          backgroundColor: "#B0BEC5",
        },
      ],
    }),
    [components]
  );

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" as const },
        tooltip: { enabled: true },
      },
      scales: {
        x: { beginAtZero: true, max: 20 },
      },
    }),
    []
  );

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>
          Score Component Weight Breakdown
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {components.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No component scores available.
          </Typography>
        ) : (
          <Box sx={{ height: Math.max(220, components.length * 48) }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        )}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          {components.map((c) => (
            <Grid item xs={12} sm={6} key={c.name}>
              <Typography variant="body2">
                <strong>{componentLabel(c.name)}</strong>: {c.points.toFixed(1)} / {c.max_points.toFixed(0)} pts
                (weight {c.weight_pct.toFixed(0)}%)
              </Typography>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function StalenessAccordion({ data }: { data: FnoTradeAnalysisResponse }) {
  const dataQualityEntries = Object.entries(data.data_quality ?? {});
  return (
    <Accordion variant="outlined" disableGutters defaultExpanded={data.is_stale || dataQualityEntries.length > 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>
          Data Staleness & Risk Warnings
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {data.is_stale ? (
            <Alert severity="warning">
              This analysis is flagged stale: {data.staleness_reason ?? "reason not specified."}
            </Alert>
          ) : dataQualityEntries.length > 0 ? (
            <Alert severity="warning" variant="outlined">
              Data is fresh, but incomplete — {dataQualityEntries.length} input
              {dataQualityEntries.length === 1 ? " is" : "s are"} unavailable
              {data.data_coverage_pct !== undefined ? ` (data coverage ${data.data_coverage_pct.toFixed(0)}%)` : ""}.
              Confidence is reduced accordingly; see the list below.
            </Alert>
          ) : (
            <Alert severity="success" variant="outlined">
              Data freshness check passed — no staleness detected, and every input was available.
            </Alert>
          )}

          <Divider />

          <Typography variant="subtitle2" gutterBottom>
            Data Quality — Unavailable Components
          </Typography>
          {dataQualityEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No data-quality limitations were reported for this analysis — every component had the data it needed.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {dataQualityEntries.map(([component, reason]) => (
                <Alert key={component} severity="info" variant="outlined">
                  <strong>{componentLabel(component)}</strong>: {reason}
                </Alert>
              ))}
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export function FnoBreakdownAccordions({ data }: { data: FnoTradeAnalysisResponse }) {
  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <TrendMomentumAccordion components={data.components} />
      <FuturesBasisAccordion components={data.components} />
      <OptionsOiAccordion components={data.components} />
      <ScoreWeightAccordion components={data.components} />
      <StalenessAccordion data={data} />
    </Stack>
  );
}

export default FnoBreakdownAccordions;
